// Amplitude Analytics
//
// Event naming conventions:
//   - Page views: automatic from route names (kebab-case). Don't add manual ones.
//   - Business events: "Past Tense Verb + Object" in Title Case.
//     e.g. "Measurement Batch Submitted", "CSV Exported", "KPI Created"
//   - Feature telemetry: "feature.action" in dot-notation snake_case.
//     e.g. "financiero.inline_edit_expense", "financiero.year_change"
//   - Properties: snake_case, always include the relevant entity ID.
//     e.g. { contract_id: 'ctr_123', kpi_count: 5, period: '2026-03' }
//   - Generic UI interactions (clicks, modals, form fills) are handled by
//     autocapture - don't add manual events for those.

import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'

let initialized = false

export function initAmplitude(): void {
  const apiKey = document.querySelector<HTMLMetaElement>('meta[name="amplitude-api-key"]')?.content
  if (!apiKey || initialized) return

  amplitude.add(sessionReplayPlugin({ sampleRate: 1 }))

  amplitude.init(apiKey, {
    autocapture: {
      elementInteractions: true,
      formInteractions: true,
      fileDownloads: true,
      sessions: true,
      pageViews: false,
    },
  })

  initialized = true
}

export function identifyUser(
  userId: string,
  properties: Record<string, string | boolean | null>,
): void {
  if (!initialized) return

  amplitude.setUserId(userId)

  const identify = new amplitude.Identify()
  for (const [key, value] of Object.entries(properties)) {
    if (value !== null) {
      identify.set(key, value)
    }
  }
  amplitude.identify(identify)
}

export function resetAmplitude(): void {
  if (!initialized) return
  amplitude.reset()
}

export function trackEvent(
  name: string,
  properties?: Record<string, unknown>,
): void {
  if (!initialized) return
  amplitude.track(name, properties)
}

export function trackPageView(
  pageName: string,
  path: string,
  properties?: Record<string, unknown>,
): void {
  if (!initialized) return
  amplitude.track('Page Viewed', { page_name: pageName, path, ...properties })
}
